import { FormEvent, useState } from 'react';
import { useLanguage } from '../../i18n/LanguageContext';
import { dealerApi, CustomerLookupResult, FoundCustomer } from '../../services/dealerApi';
import { dealerErrorMessage, errorCodeOf, isE164, isEmail } from '../../utils/dealer';

interface CustomerLookupProps {
  token: string;
  /** The customer the dealer confirmed, or null while searching. */
  confirmed: FoundCustomer | null;
  onConfirm: (customer: FoundCustomer) => void;
  /** The dealer wants to pick another customer. */
  onChange: () => void;
  idPrefix: string;
}

/**
 * Spec 085 BR-8 / D-152 (Maher, 2026-11-03) — step 1 of request credit, assign credit and book: find a registered
 * Garagat customer by the email AND mobile number they registered (both required). A match shows
 * "<Name> · Garagat customer" with a confirm; no match explains calmly that the buyer must sign up first. Credit
 * never goes to a bare phone. The contacts stay with the confirmed customer because the follow-up call sends them
 * again and the server re-checks them against `customerRef`.
 */
export default function CustomerLookup({ token, confirmed, onConfirm, onChange, idPrefix }: CustomerLookupProps) {
  const { t } = useLanguage();
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [result, setResult] = useState<CustomerLookupResult | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  if (confirmed) {
    return (
      <div className="dealer-lookup-found" data-testid="customer-confirmed">
        <span>
          <strong>{confirmed.displayName || t('dealer.credits.unnamed')}</strong>
          {' · '}
          <span>{t('dealer.lookup.garagatCustomer')}</span>
        </span>
        <button type="button" className="btn btn-secondary" onClick={() => { setResult(null); onChange(); }}>
          {t('dealer.lookup.change')}
        </button>
      </div>
    );
  }

  const search = async (e: FormEvent) => {
    e.preventDefault();
    const errs: Record<string, string> = {};
    if (!isEmail(email)) errs.email = t('dealer.validation.email');
    if (!isE164(phone)) errs.phone = t('dealer.validation.phone');
    setFieldErrors(errs);
    setResult(null);
    setNotice(null);
    setError(null);
    if (Object.keys(errs).length > 0) return;
    setBusy(true);
    try {
      setResult(await dealerApi.lookupCustomer({ email: email.trim(), phone: phone.trim() }, token));
    } catch (err) {
      const code = errorCodeOf(err);
      if (code === 'CUSTOMER_NOT_FOUND') setNotice(t('dealer.lookup.notFound'));
      else if (code === 'TOO_MANY_LOOKUPS') setNotice(t('dealer.lookup.tooMany'));
      else setError(dealerErrorMessage(err, t));
    } finally {
      setBusy(false);
    }
  };

  const edit = (setter: (v: string) => void) => (v: string) => { setter(v); setResult(null); setNotice(null); };

  return (
    <form className="dealer-lookup" onSubmit={search} noValidate>
      <h3>{t('dealer.lookup.title')}</h3>
      <p className="dealer-muted">{t('dealer.lookup.hint')}</p>
      <div className="form-group">
        <label htmlFor={`${idPrefix}-email`}>{t('dealer.lookup.email')}</label>
        <input id={`${idPrefix}-email`} type="email" dir="ltr" autoComplete="off" value={email} maxLength={254}
          onChange={(e) => edit(setEmail)(e.target.value)} />
        {fieldErrors.email && <div className="dealer-field-error">{fieldErrors.email}</div>}
      </div>
      <div className="form-group">
        <label htmlFor={`${idPrefix}-phone`}>{t('dealer.form.buyerPhone')}</label>
        <input id={`${idPrefix}-phone`} type="tel" dir="ltr" autoComplete="off" value={phone} placeholder="+971501234567"
          onChange={(e) => edit(setPhone)(e.target.value)} />
        {fieldErrors.phone && <div className="dealer-field-error">{fieldErrors.phone}</div>}
      </div>
      {notice && <p className="dealer-lookup-notice" role="status">{notice}</p>}
      {error && <div className="error-message">{error}</div>}
      {result ? (
        <div className="dealer-lookup-found" role="status" data-testid="customer-found">
          <span>
            <strong>{result.displayName || t('dealer.credits.unnamed')}</strong>
            {' · '}
            <span>{t('dealer.lookup.garagatCustomer')}</span>
          </span>
          <button type="button" className="btn btn-primary"
            onClick={() => onConfirm({ customerRef: result.customerRef, displayName: result.displayName, email: email.trim(), phone: phone.trim() })}>
            {t('dealer.lookup.confirm')}
          </button>
        </div>
      ) : (
        <button type="submit" className="btn btn-primary" disabled={busy}>
          {busy ? t('dealer.common.loading') : t('dealer.lookup.find')}
        </button>
      )}
    </form>
  );
}
